import { Component, Input, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";
import { selectCostBasis, selectFrequency, selectInternalTransfersVerifyCallFinished, selectMultipleToAccountsPositionLotTableData, selectToAccountsInfo } from "../../reducers";
import { Observable, Subject, map, take, takeUntil } from "rxjs";
import { RowDataById, ToAccountInfoById } from "../../models/internal-transfer/InternalTransfersInfo";
import { CellClassParams, ColDef, GridApi, GridOptions, GridReadyEvent, Module, RowHeightParams, ClientSideRowModelModule, ValidationModule } from "ag-grid-community";
import { agFilterNames } from "../../../shared/types/AgFilterNames";
import { PositionsLotsTableColumnMultipleAccounts } from "../../../shared/types/PositionsLotsTableColumn";
import { positionConstants, RegTypeCode } from "../../../shared/utilities/Constants";
import { TableColumn } from "../../../shared/types/TableColumn";
import { costBasisHeaderTemplate, currPricePerShareHeaderTemplate, estMarketValHeaderTemplate, positionSymbolHeaderTemplate, totalCostBasisHeaderTemplate } from "./table-helpers"
import { AllEnterpriseModule, ExcelExportModule, MasterDetailModule, RowGroupingModule } from "ag-grid-enterprise";
import { TextFloatingFilterComponent } from "../../../shared/components/text-floating-filter/text-floating-filter.component";
import { SetFloatingFilterComponent } from "../../../shared/components/set-floating-filter/set-floating-filter.component";
import { CurrencyPipe, DecimalPipe } from "@angular/common";
import { selectClassifications } from "../../../account-info-header/reducers";
import { Classifications } from "../../../account-info-header/models";
import { ReviewPageActions } from "../../actions";
import { AdobeConstants } from "../../../shared/adobe-tagging/AdobeConstants";
import { AdobeHelpers } from "../../../shared/adobe-tagging/adobeHelper";
import { ReviewInputPositionObservableService } from "./reviewInputPositionObservableService";
import { FeatureFlagService } from "../../../shared/services/feature-flag-service";
import { Acknowledgement } from "../../models/review-page/ReviewPage";

@Component({
  standalone: false,
  selector: 'mm-positions-table-multiple-review',
  templateUrl: './positions-table-multiple-review.component.html',
  styleUrls: ['./positions-table-multiple-review.component.scss'],
})
export class PositionsTableMultipleReviewComponent implements OnInit {
 @Input() isInitiateAndReleaseFlowFromTable: boolean;
  toAccounts: string[];
  toAccountsById: ToAccountInfoById;
  toAccountsData: {
    accountId: string;
    accountRegistration: string;
    regType: string;
    rowData$: Observable<PositionsLotsTableColumnMultipleAccounts[]> | undefined;
  }[] = [];
  classifications: Classifications = new Classifications();
  positionsLotDetails: RowDataById;
  positionsLotDetails$: Observable<RowDataById>;
  gridApis: GridApi[] = [];
  costBasisMethod: string | undefined;
  isManualLotSelection: boolean = false;
  destroy$: Subject<boolean> = new Subject<boolean>();
  gridOptions: GridOptions;
  frameworkComponents: any;
  gridOptionsTotals: GridOptions = {theme: 'legacy'};
  gridApiTotals: GridApi[] = [];
  expanded: boolean[] = [];
  gridColumnApiTotals: GridApi | undefined | null;
  positionsViewPageBannerMessageText: string = positionConstants.PositionsViewPageBannerMessageText;
  taxesApplicableViewPageBannerMessageText: string = positionConstants.TaxesApplicableViewPageBannerMessageText;
  selectedFrequency: string;
  quantityMismatch: string = positionConstants.positionMismatchRestriction;
  isOneTimeTotalDist: boolean = false;
  noPositionsMessage = positionConstants.AccountHasNoPositionsMessage;
  is401kToAccount: boolean;

  icons = {
    sortAscending: '<i class="sch-caret-up "/>',
    sortDescending: '<i class="sch-caret-down"/>',
    filter: '<i class="sch-search" />',
    sortUnSort: '<i class="sch-caret-down"/>',
  };
  colNames = {
    positionSymbol: 'Symbol',
    positionName: 'Name',
    availableShares: 'Available shares',
    currentPricePerShare: 'Current price/share',
    costBasis: 'Cost basis',
    sharesToMove: 'Shares to move',
    totalCostBasis: 'Total cost basis',
    estimatedMarketValue: 'Estimated market value',
  };
  totalsRowdata = [
    {
      Symbol: '',
      Name:'',
      RowHeight: 29,
      'Available shares': '',
      'Current price/share': '',
      'Cost basis': 'Totals',
      'setToAverage': '',
      'Quantity': '',
      'cmtSequenceId': '',
      'schwabSecurityId': '',
      'Estimated market value':'',
      'Total cost basis':'',
      'Shares to move':'',
    }
  ];

  public globalModules: Module[] = [
    ValidationModule,
    AllEnterpriseModule,
    ClientSideRowModelModule
  ]

  public modules: Module[] = [
    ...this.globalModules,
    RowGroupingModule,
    MasterDetailModule, 
    ExcelExportModule,
  ];

  defaultColumnDefs: ColDef = {
    filter: agFilterNames.text,
    sortingOrder: ['asc', 'desc'],
    suppressHeaderMenuButton: true,
    suppressHeaderContextMenu: true,
    suppressHeaderFilterButton: true,
    sortable: true,
    unSortIcon: true,
    flex: 1,
    minWidth: 29,
  };
  columnDefs: ColDef[] = [];
  formatValues(params: any) {
    if (params.value == null || params.value === '' || params.value === 'N/A') return params.value;
    const numValue = typeof params.value === 'string' ? parseFloat(params.value.replace(/[^0-9.-]/g, '')) : params.value;
    if (isNaN(numValue)) return params.value;
    return `$${numValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  }
  defColumnDefs: ColDef[] = [
    {
        field: this.colNames.positionSymbol,
        width: 145,
        minWidth: 145,
        lockPosition: true,
        cellRendererSelector: (params) => {
          if (this.isMasterDetail()) {
            return {
              component: 'agGroupCellRenderer'
            }
          }
        }
    },
    {
        field: this.colNames.positionName,
        width: 250,
        minWidth: 250,
        lockPosition: true,
        headerClass: 'middle-col-header'
    },
    {
        field: this.colNames.availableShares,
        headerName: this.colNames.availableShares,
        width: 150,
        minWidth: 150,
        lockPosition: true,
        headerClass: 'middle-col-header',
        comparator: (valueA: string, valueB: string) => {
          const a = valueA.replace(',', '');
          const b = valueB.replace(',', '');
          const sharesA = parseFloat(a.substring(0));
          const sharesB = parseFloat(b.substring(0));
          return sharesA - sharesB;
        },
        cellClass: params => {
          if (params.data['Name'] === 'Totals') {return 'leftAlignClass';}
          if (params.value.charAt(0) === '-') { return 'shortPositionText';} 
          return '';
        },
    },
    {
        field: this.colNames.currentPricePerShare,
        headerName: this.colNames.currentPricePerShare,
        type: 'rightAligned',
        width: 160,
        minWidth: 160,
        lockPosition: true,
        comparator: this.currencyComparator,
        headerComponentParams: {
          template: currPricePerShareHeaderTemplate
        }
    },
    {
        field: this.colNames.costBasis,
        headerName: this.colNames.costBasis,
        type: 'rightAligned',
        width: 145,
        minWidth: 145,
        lockPosition: true,
        comparator: this.currencyComparator,
        headerComponentParams: {
          template: costBasisHeaderTemplate
        },
        cellClass: params => {
          if (params.data['Cost basis'] === 'Totals') { return 'totalsRowText alignTotalsRight';}
          if (params?.value?.charAt(0) === '-') {return 'alignTotalsRight shortPositionText';}
          return 'alignTotalsRight';
        },
    },
    
    {
        field: this.colNames.sharesToMove,
        headerName: this.colNames.sharesToMove,
        width: 125,
        minWidth:125,
        lockPosition: true,
        cellClass: params => {
          return params.data['Cost basis'] === 'Totals' ? 'totalsRowText': '';
        },
    },
    {
        field: this.colNames.totalCostBasis,
        headerName: this.colNames.totalCostBasis,
        type: 'rightAligned',
        width: 145,
        minWidth: 150,
        lockPosition: true,
        comparator: this.currencyComparator,
        headerComponentParams: {
          template: totalCostBasisHeaderTemplate
        },
        valueFormatter: this.formatValues,
        cellClass: params => {
          if (params.data['Name'] === 'Totals') { return 'totalsRowText alignTotalsRight';}
          const value = params?.value;
          if (value && value !== 'N/A' && value !== '') {
            const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;
            if (!isNaN(numValue) && numValue < 0) {
              return 'shortPositionText alignTotalsRight';
            }
          }
          return 'alignTotalsRight';
        },
    },
    {
        field: this.colNames.estimatedMarketValue,
        headerName: 'Est. mkt val',
        width: 150,
        minWidth: 150,
        comparator: this.currencyComparator,
        type: 'rightAligned',
        lockPosition: true,
        headerComponentParams: {
          template: estMarketValHeaderTemplate
        },
        cellClass: params => {
          if (params.data['Name'] === 'Totals') { return 'totalsRowText alignTotalsRight';}
          if (params?.value?.charAt(0) === '-') { return 'shortPositionText alignTotalsRight';}
          return 'alignTotalsRight';
        },
    },
  ];

  public detailCellRendererParams: any = {
    detailGridOptions: {
      rowHeight: 29,
      headerHeight: 29,
      sizeColumnsToMatchMasterTable(gridColumnApi: GridApi) {
        this.columnDefs.forEach((lotColumn) => {
          lotColumn.width = gridColumnApi?.getColumn(this.getMasterColumn(lotColumn))?.getActualWidth() ?? lotColumn.width;
        });
      },
      getMasterColumn(lotColumn: any): string {
        if (lotColumn.colId === 'positonSymbol') {
          return this.colNames.positionSymbol;
        }
        if (lotColumn.field === 'Acquired date') {
          return this.colNames.positionName;
        }
        if (lotColumn.field === 'Holding Period') {
          return this.colNames.availableShares;
        }
        if (lotColumn.field === 'Current price/share') {
          return this.colNames.currentPricePerShare;
        }
        if (lotColumn.field === 'Cost Basis') {
          return this.colNames.costBasis;
        }
        if (lotColumn.field === 'Lots to move') {
          return this.colNames.sharesToMove;
        }
        if (lotColumn.field === 'Total cost basis') {
          return this.colNames.totalCostBasis;
        }
        if (lotColumn.field === 'Estimated market value') {
          return this.colNames.estimatedMarketValue;
        }
        return '';
      },
      colNames: {
        expandCollapse: 'expandCollapse',
        positionSymbol: 'Symbol',
        positionName: 'Name',
        availableShares: 'Available shares',
        currentPricePerShare: 'Current price/share',
        costBasis: 'Cost basis',
        sharesToMove: 'Shares to move',
        totalCostBasis: 'Total cost basis',
        estimatedMarketValue: 'Estimated market value',
        acquiredDate: 'Acquired Date',
        holdingPeriod: 'Holding Period'
      },
      columnDefs: [
        {
          field: '',
          headerName: '',
          colId: 'positonSymbol',
          filter: false,
          width:100,
          headerClass: 'detailSubHeadernoBorder',
          cellStyle: {
            backgroundColor: '#F1F1F1',
          },
          headerComponentParams: {
            template:
            `<div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
                  <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
              </div>`
          }
          
        },
        {
          field: 'Acquired date',
          headerName: 'Acquired Date',
          type: 'leftAligned',
          width:100,
          icons: {
            sortAscending: '<i class="sch-caret-up"/>',
            sortDescending: '<i class="sch-caret-down"/>',
          },
          headerClass: 'detailSubHeadernoBorder',
          filter: false,
          sortable: true,
          sort: 'desc',
          sortingOrder: ['asc', 'desc'],
          comparator: (valueA: string, valueB: string) => (new Date(valueA) > new Date(valueB)) ? 1 : -1,
          cellClass: 'subGridCell ',
          headerComponentParams: {
            template:
            `<div data-ref="eLabel" class="ag-header-cell-label" style="position:absolute; padding-top: 6px;">
                <span data-ref="eText" class="subGridheader"></span>
                <span data-ref="eSortAsc"></span>
                <span data-ref="eSortDesc"></span>
              </div>`
            },
        },
        {
          field: 'Holding Period',
          headerName: '',
          width:100,
          sortable: false,
          filter: false,
          headerClass: 'detailSubHeadernoBorder',
          suppressHeaderFilterButton: true,
          suppressHeaderMenuButton: true,
          cellClass: (params : CellClassParams) => {
            return (params?.value?.charAt(0) === '-') ? ['subGridCell', 'shortPositionText'] : 'subGridCell';
          },
        },
        {
          field: 'Current price/share',
          headerName: '',
          headerClass: 'detailSubHeadernoBorder',
          width:100,
          filter: false,
          suppressHeaderFilterButton: true,
          sortable: false,
          type: 'rightAligned',
          cellClass: 'subGridCellV2',
          headerComponentParams: {
            template:
            `<div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
                  <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
              </div>`
          }
        },
        {
          field: 'Cost Basis',
          headerName: '',
          width:100,
          headerClass: 'detailSubHeadernoBorder',
          sortable: false,
          type: 'rightAligned',
          suppressHeaderMenuButton: true,
          suppressHeaderFilterButton: true,
          filter: false,
          valueFormatter: this.formatValues,
          cellClass: (params : CellClassParams) => {
            return (params?.value?.charAt(0) === '-') ? ['subGridCellV2', 'shortPositionText', 'alignTextRight'] : ['subGridCellV2', 'alignTextRight'];
          },
        },
        {
          field: 'Lots to move',
          headerName: '',
          type: 'rightAligned',
          headerClass: 'detailSubHeadernoBorder',
          suppressHeaderFilterButton: true,
          suppressHeaderMenuButton: true,
          width:100,
          filter: false,
          cellClass: ['alignTextLeft','subGridCell'],
          sortable: false,
          editable: false,
          singleClickEdit: true,
        },
        {
          field: 'Total cost basis',
          headerName: '',
          headerClass: 'detailSubHeadernoBorder',
          type: 'rightAligned',
          filter: false,
          suppressHeaderFilterButton: true,
          suppressHeaderMenuButton: true,
          width:100,
          valueFormatter: this.formatValues,
          cellClass: (params : CellClassParams) => {
            const value = params?.value;
            if (value && value !== 'N/A' && value !== '') {
              const numValue = typeof value === 'string' ? parseFloat(value.replace(/[^0-9.-]/g, '')) : value;
              if (!isNaN(numValue) && numValue < 0) {
                return ['subGridCell', 'shortPositionText', 'alignTextRight'];
              }
            }
            return ['subGridCell', 'alignTextRight'];
          },
          sortable: false,
          editable: false,
          singleClickEdit: true,
        },
        {
          field: 'Estimated market value',
          headerName: '',
          headerClass: 'detailSubHeadernoBorder',
          width:100,
          sortable: false,
          suppressHeaderFilterButton: true,
          suppressHeaderMenuButton: true,
          type: 'rightAligned',
          filter: false,
          cellClass: (params : CellClassParams) => {
            return (params?.value?.charAt(0) === '-') ? ['subGridCellV2', 'shortPositionText', 'alignTextRight'] : ['subGridCellV2', 'alignTextRight'];
          },
        },
      ]
    },
    
    getDetailRowData: (params: any) => {
      const lotsResponse: any[] = Array.of(params.data.lotDetail);
      let i = 0
      let fullLotDetails: Array<any> = [];
      let lotDetail: Array<any> = [];
      while (lotsResponse[0].length > i) {
        lotDetail = lotsResponse.map(position => {
          return {
            'Acquired date': position[i].acquiredDate,
            'Holding Period': this.decimalPipe.transform(position[i].lotQuantity, '1.0-6') + ' (' + position[i].holdingPeriod.toLowerCase() + '-term' + ')',
            'Current price/share': params.data['Current price/share'],
            'Cost Basis': (this.currencyPipe.transform(position[i].costBasis)),
            'Estimated market value': this.currencyPipe.transform(Number(params.data['Current price/share'].replace('$','').replaceAll(',','')) * Number(position[i]['lotsToMove']) *  Number(position[i]['valMultiplier']) * Math.sign(position[i].lotQuantity)),
            'setToAverage': position.setToAverage,
            'Lots to move': position[i]['lotsToMove'],
            'restrictions': params.data.restrictions,
            'masterNode': params,
            'lotId': position[i]['lotId'],
            'valMultiplier': position[i]['valMultiplier'],
            'Total cost basis': position[i]['Total cost basis']
          }
        })
        i++;
        fullLotDetails = fullLotDetails.concat(lotDetail);
      }
      params.successCallback(fullLotDetails);
    }
  };

  onEditClicked(): void {
    this.store.dispatch(ReviewPageActions.clearAttentionMessage());
    AdobeHelpers.fireClick(AdobeConstants.LINKID + AdobeConstants._EDIT);
    this.store.dispatch(ReviewPageActions.editClicked());
    this.editPositionClicked()
    let acknowledgement: Acknowledgement = {
      isAcknowledgementReviewed: false,
      acknowledgementConsentTime: ""
    }
    this.store.dispatch(ReviewPageActions.updateAcknowledgement({ acknowledgement }));
  }

  editPositionClicked(){
    setTimeout(() => {
      this.reviewInputPositionObserbableService.emitData('clicked')
    }, 100);
  }


  updateTotalsFooters() {
    setTimeout(() => {
      for (let i = 0; i < this.gridApis.length; i++) {
        let total = 0;
        let totalEmv = 0
        let totalCostBasis = 0
        this.gridApis[i]?.forEachNode(node => {
          total = Number(node.data['Shares to move']) + total;
          totalEmv = Number(node.data['Estimated market value'].replace('$', '').replaceAll(',', '')) + totalEmv;
          if (node.data['Total cost basis'] !== undefined && node.data['Total cost basis'] !== '' && node.data['Total cost basis'] !== 'N/A') {
            totalCostBasis = Number(node.data['Total cost basis'].replace('$', '').replaceAll(',', '')) + totalCostBasis
          }
          this.gridApiTotals[i]?.forEachNode(totalsRow => {
            totalsRow.setDataValue("Shares to move", total.toLocaleString())
            totalsRow.setDataValue("Estimated market value", this.currencyPipe.transform(totalEmv))
            totalCostBasis !== 0 ? totalsRow.setDataValue("Total cost basis", this.currencyPipe.transform(totalCostBasis)) : totalsRow.setDataValue("Total cost basis", '')
          })
        })

      }

    }, 1);
  }

  constructor(
    private store: Store,
    private currencyPipe: CurrencyPipe,
    private decimalPipe: DecimalPipe,
    private reviewInputPositionObserbableService: ReviewInputPositionObservableService,
  ) {}

  ngOnInit(): void {
    this.frameworkComponents = {
      customTextFloatingFilter: TextFloatingFilterComponent,
      customSetFloatingFilter: SetFloatingFilterComponent,
    };
    this.columnDefs = this.defColumnDefs;
    this.positionsLotDetails$ = this.store.select(
      selectMultipleToAccountsPositionLotTableData
    );

    this.store.select(selectCostBasis).pipe(takeUntil(this.destroy$)).subscribe(info => {
      this.costBasisMethod = info;
      if (this.costBasisMethod === "None"){
        this.isManualLotSelection = true;
      }
    });
    
    this.store.select(selectFrequency).pipe(takeUntil(this.destroy$)).subscribe((frequency) => {
      this.isOneTimeTotalDist = frequency === 'onetimeTotalDist';
      this.selectedFrequency = frequency ?? '';
    })

    this.store
      .select(selectMultipleToAccountsPositionLotTableData)
      .pipe(takeUntil(this.destroy$))
      .subscribe((positionsLotDetails) => {
        this.positionsLotDetails = positionsLotDetails;
      });

    this.store
      .select(selectToAccountsInfo)
      .pipe(takeUntil(this.destroy$))
      .subscribe((toAccounts) => {
        this.toAccountsById = toAccounts;
        this.toAccounts = Object.keys(toAccounts);
      });

    this.store.select(selectToAccountsInfo).pipe(takeUntil(this.destroy$)).subscribe(toAccountInfo => {
      this.is401kToAccount = Object.values(toAccountInfo).some(info => (info?.registrationType === RegTypeCode.INDIVIDUAL_401K || info?.registrationType === RegTypeCode.ROTH_401K ));  
    });
    
    this.store
      .select(selectClassifications)
      .pipe(takeUntil(this.destroy$))
      .subscribe((classifications) => {
        this.classifications = classifications;
      });

      this.store
      .select(selectInternalTransfersVerifyCallFinished)
      .pipe(takeUntil(this.destroy$))
      .subscribe((verifycallfinished) => {
      if (verifycallfinished && this.isInitiateAndReleaseFlowFromTable){
          this.mapAccountData();
      }
      });
      if(!this.isInitiateAndReleaseFlowFromTable || this.is401kToAccount){
        this.mapAccountData();
      }
      this.gridOptions = {
        ...this.gridOptions,
        theme: 'legacy',
        onGridReady: (params) => {
          params.api.applyColumnState({
            state: [{
              colId: this.colNames.positionSymbol,
              sort: 'asc'
            }],
            applyOrder: true
          })
        }
      }
  }

  public mapAccountData(){
    this.toAccounts.forEach((account) => {
      this.toAccountsData.push({
        accountId: account,
        accountRegistration: this.toAccountsById[account].accountRegistration[0],
        regType: this.toAccountsById[account].regTypeShortName,
        rowData$: new Observable<PositionsLotsTableColumnMultipleAccounts[]>(),
      });
    });

    this.toAccountsData = this.toAccountsData.map((account) => {
      account.rowData$ = this.getRowData(account.accountId);
      return account;
    });
  }

  public onGridReady(params: GridReadyEvent) {
    this.gridApis.push(params.api);
    if (this.costBasisMethod === 'None') {
      params.api?.forEachNode(node => {
        node.setExpanded(true)
        this.expanded.push(true);
      })
    }
    this.updateTotalsFooters()
    params.api.sizeColumnsToFit()
    setTimeout(() => {
      this.detailCellRendererParams.detailGridOptions?.sizeColumnsToMatchMasterTable(params.api);
      params.api.redrawRows()
    }, 1000);
  }

  getRowHeight = (params: RowHeightParams) => params.data.RowHeight;

  currencyComparator(valueA: string | number, valueB: string | number) {
    if (typeof valueA === 'string' && typeof valueB === 'string') {
      valueA = +valueA.replace(/[^\-\w\s^.]/gi, '');
      valueB = +valueB.replace(/[^\-\w\s^.]/gi, '');
    }
    return +valueA - +valueB;
  }

  getRowData(accountNumber: string): Observable<PositionsLotsTableColumnMultipleAccounts[]> | undefined {
    return this.positionsLotDetails$.pipe(
      take(1),
      map((details: RowDataById) => {
        if (details[accountNumber]?.positionData) {
          return this.mapPositionLotDetailsReview(details[accountNumber]?.positionData);
        }
        return [];
      })
    );
  }

  public onGridReadyTotals(params: GridReadyEvent) {
    this.gridOptionsTotals = params;
    this.gridApiTotals.push(params.api);
    this.gridColumnApiTotals = params.api;
    params.api.setGridOption('headerHeight', 0);
    params.api.sizeColumnsToFit();
  }

  public mapPositionLotDetailsReview(positions: TableColumn[]): any[] {
    let data = positions.map((position) => {
      const restrictionText = position['restrictions']?.restrictionText ?? '';
      const hasAllowedRestriction = !position['restrictions']?.isRestricted
        || restrictionText === this.quantityMismatch
        || restrictionText.startsWith('DUPLICATE');

      if (position['Shares to move'] !== '' && hasAllowedRestriction) {
        let availableShares = (this.isInitiateAndReleaseFlowFromTable && !this.is401kToAccount)
          ? `${this.decimalPipe.transform(position['Available shares'], '1.0-5')} (${position.numberOfLots} ${position.numberOfLots === 1 ? 'lot' : 'lots'})`
          : position['Available shares'];
        const currentPricePerShare = Number(position['Current price/share'].replace('$','').replaceAll(',',''));
        let availbleSharesSign = Math.sign(Number(position['Available shares'].split(' ')[0].replaceAll(',',''))); // used for making emv negative if the position is a short.
        const estimatedMarketValue = this.selectedFrequency === 'onetimeTotalDist'
           ? this.currencyPipe.transform((availbleSharesSign * currentPricePerShare * Number(position['Shares to move']) * Number(position['valMultiplier'])))?.toString()
           : position['Estimated market value'].toString();
        return {
          Symbol: position['Symbol'],
          Name: position['Name'],
          'Available shares': availableShares,
          'Current price/share': position['Current price/share'],
          'Cost basis': position['Cost basis'],
          'Shares to move': position['Shares to move'],
          'Total cost basis': this.selectedFrequency === 'onetimeTotalDist' ? position['Cost basis'] : position['Total cost basis'] === "" ? 'N/A' : position['Total cost basis'],
          'Estimated market value': estimatedMarketValue,
          'lotDetail': this.filterOutEmptyLotsToMove(position['lotDetail'], position['valMultiplier']),
          RowHeight: positionConstants.tableRowHeight
        };
      }
    });
    data = data.filter(Boolean);
    this.reorderPositions(data);
    return data;
  }

  reorderPositions(positions: any[]): any[]{
    positions.sort((a, b) => {
      return a.Symbol.localeCompare(b.Symbol);
    });
    return positions;
  }

  filterOutEmptyLotsToMove(pos: any[], valMultiplier: any) {
    let lotData = pos.map((lot: any) => {
      if ((lot['lotsToMove'] !== '') && (lot['lotsToMove'] !== undefined)) {
        return {
          'acquiredDate': lot['acquiredDate'],
          'costBasis': lot['costBasis'],
          'holdingPeriod': lot['holdingPeriod'],
          'purchasePrice': lot['purchasePrice'],
          'purchaseDate': lot['purchaseDate'],
          'lotQuantity': lot['lotQuantity'],
          'lotId': lot['lotId'],
          'lotsToMove': lot['lotsToMove'],
          'Total cost basis': lot['totalCostBasis'],
          'valMultiplier': valMultiplier,
        };
      }
    });
    lotData = lotData.filter(Boolean);
    return lotData;
  }

  isMasterDetail(){
    if(this.costBasisMethod === 'None'){
      return true
    }
    return false
  }

  expandOrCollapse(i: number) {
    this.expanded[i] = !this.expanded[i];
    this.gridApis[i]?.forEachNode((rowNode, index) => {
      if (this.expanded[i]) {
        rowNode.setExpanded(true);
      }
      else {
        rowNode.setExpanded(false);
      }
    });
  }
}